import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Usuario } from 'src/app/core/models/usuario.model';
import { AuthenticationService, segundosRetryAfter } from 'src/app/core/services/authentication.service';
import { BackgroundImageService } from 'src/app/core/services/background-image.service';
import { WeblabService } from 'src/app/core/services/weblab.service';
import { EmpresaResponse } from 'src/app/core/models/response/empresa-response.model';
import { UsuarioResponse } from 'src/app/core/models/response/usuario-response.model';
import { LoginResponse } from 'src/app/core/models/response/login-response.model';
import { environment } from 'src/environments/environment';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  img: string = '';
  usuario: Usuario = new Usuario();
  value!: string;
  erroLogin: string = '';
  loginBloqueado: boolean = false;
  private desbloqueioTimer: any;
  empresaErro: boolean = false;
  telefoneFaleConosco: string;
  tokenAcesso: string;
  paramTokenAcesso: boolean = true;
  loading: boolean = false;
  loadingEmpresa: boolean = false;
  imagemLogoBase64: any;
  empresaNome: string = '';

  constructor(
    private backgroundImageService: BackgroundImageService,
    private weblabService: WeblabService,
    private authenticationService: AuthenticationService,
    private route: ActivatedRoute,
    private router: Router) {

  }

  ngOnInit(): void {
    this.img = this.backgroundImageService.randonImage();

    this.tokenAcesso = this.route.snapshot.params['chave'];
    this.paramTokenAcesso = !!this.tokenAcesso;

    if (this.paramTokenAcesso) this.getEmpresa();
    else this.authenticationService.limparCacheEmpersa();

    this.authenticationService.tokenExpiradoEmit(false);

    if (this.authenticationService.mensagemEncerramento) {
      this.erroLogin = this.authenticationService.mensagemEncerramento;
      this.authenticationService.mensagemEncerramento = '';
    }
    this.agendarDesbloqueio();
  }

  ngOnDestroy(): void {
    clearTimeout(this.desbloqueioTimer);
  }

  selecionarCodigoEmpresa() {
    this.getEmpresa();
  }

  getEmpresa() {
    let empresa = this.authenticationService.obterEmpresa;

    if (empresa && this.tokenAcesso === empresa.token_acesso) {
      this.getEmpresaSucesso(empresa);

      return;
    }

    this.loadingEmpresa = true;
    this.weblabService.getEmpresa(this.tokenAcesso).subscribe(
      (empresa: EmpresaResponse) => this.getEmpresaSucesso(empresa),
      (erro: any) => this.getEmpresaErro(erro));
  }

  private getEmpresaSucesso(empresa: EmpresaResponse) {
    this.empresaNome = empresa?.dados?.empresa || "";
    empresa.token_acesso = this.tokenAcesso;
    this.authenticationService.salvarEmpresa(empresa);

    this.loadingEmpresa = false;
    this.empresaErro = false;
    this.paramTokenAcesso = !!this.tokenAcesso;
  }

  private getEmpresaErro(erro: any) {
    setTimeout(() => {
      this.loadingEmpresa = false;
      this.paramTokenAcesso = false;
      this.empresaErro = true;
    }, 1000);
  }

  logar() {
    this.usuario.senha = this.usuario?.senha.replace(/^\s+|\s+$/g, '');
    if (!this.usuario?.usuario || !this.usuario?.senha || !this.tokenAcesso) return;

    // 429: nenhuma tentativa manual antes do Retry-After.
    const restante = this.segundosBloqueioLogin();
    if (restante > 0) {
      this.erroLogin = this.mensagemLimiteLogin(restante);
      return;
    }

    this.erroLogin = '';
    this.loading = true;

    this.weblabService.login(this.usuario, this.tokenAcesso).subscribe({
      next: (token: LoginResponse) => this.loginSucesso(token),
      error: (erro: any) => this.loginErro(erro),
    });
  }

  private loginSucesso(token: LoginResponse) {
    this.authenticationService.salvarToken(token);
    this.authenticationService.tokenExpiradoEmit(false);
    this.buscaUsuarioLogado();
  }

  private loginErro(erro: HttpErrorResponse) {
    if (erro?.status === 429) {
      const segundos = segundosRetryAfter(erro);
      if (segundos) this.authenticationService.loginBloqueadoAte = Date.now() + segundos * 1000;
      this.agendarDesbloqueio();
    }
    this.erroLogin = this.mensagemErroLogin(erro);
    this.loading = false;
  }

  private segundosBloqueioLogin(): number {
    return Math.max(0, Math.ceil((this.authenticationService.loginBloqueadoAte - Date.now()) / 1000));
  }

  private agendarDesbloqueio() {
    clearTimeout(this.desbloqueioTimer);
    const restante = this.segundosBloqueioLogin();
    this.loginBloqueado = restante > 0;
    if (this.loginBloqueado) this.desbloqueioTimer = setTimeout(() => this.agendarDesbloqueio(), restante * 1000);
  }

  private mensagemLimiteLogin(segundos: number): string {
    if (!segundos) return 'Muitas tentativas de login. Aguarde e tente novamente.';
    const espera = segundos < 60
      ? `${segundos} segundo${segundos > 1 ? 's' : ''}`
      : `${Math.ceil(segundos / 60)} minuto${Math.ceil(segundos / 60) > 1 ? 's' : ''}`;
    return `Muitas tentativas de login. Aguarde ${espera} e tente novamente.`;
  }

  // 401 do login é falha de credencial (sem refresh); 429 respeita Retry-After;
  // indisponibilidade não é apresentada como senha incorreta.
  private mensagemErroLogin(erro: HttpErrorResponse): string {
    if (erro?.status === 401) return 'Usuário ou senha inválido!';
    if (erro?.status === 429) return this.mensagemLimiteLogin(segundosRetryAfter(erro));
    return erro?.error?.erro?.mensagem && erro.status !== 503 && erro.status !== 0
      ? erro.error.erro.mensagem
      : 'Autenticação temporariamente indisponível. Tente novamente em instantes.';
  }

  buscaUsuarioLogado() {
    this.getUsuarioSucesso({
      empresa_usuario_oticomlab: this.empresaNome,
      usuario_oticomlab: this.usuario.usuario
    });
    // this.weblabService.getUsuario().subscribe({
    //   next: (usuario: UsuarioResponse) => this.getUsuarioSucesso(usuario),
    //   error: (erro: any) => this.getUsuarioErro(erro),
    // });
  }

  private getUsuarioSucesso(usuario: UsuarioResponse) {
    this.authenticationService.salvarUsuarioLogado(this.usuario.usuario, usuario);
    this.loading = false;
    this.router.navigate([environment.router.dashboard]);
  }

  private getUsuarioErro(erro: any) {
    if (erro.error?.empresa_usuario_weblab) {
      this.authenticationService.salvarUsuarioLogado(this.usuario.usuario, {
        empresa_usuario_oticomlab: erro.error.empresa_usuario_weblab,
        usuario_oticomlab: erro.error.usuario_weblab
      });
    }

    this.loading = false;
    this.router.navigate([environment.router.dashboard]);
  }

  changeUsuario() {
    this.authenticationService.usuario = this.usuario.usuario;
  }
}